import { Resource, Reference } from 'fhir/r4';
import { UtilityService } from './utility.service';
import { StateService } from './state.service';

export interface ResourceContextOverrides {
    patient?: Reference | null;
    encounter?: Reference | null;
    practitioner?: Reference | null;
    practitionerId?: string | null;
}

export interface ResourceContextReferences {
    patient?: Reference;
    encounter?: Reference;
    practitioner?: Reference;
}

export abstract class FhirResourceConversionBaseService<TResource extends Resource = Resource> {
    protected constructor(
        protected readonly utility: UtilityService,
        protected readonly stateService: StateService
    ) { }

    abstract convertFhirResourceToDynamicFormFormat(resource: TResource | null | undefined): any;

    abstract convertDynamicFormFormatToFhirResource(
        dynamicFormData: any,
        existing?: TResource | null
    ): TResource;

    abstract eliminateEmptyFieldsInShallowAndDeepPaths(resource: TResource): TResource;

    abstract attachAppStateContext(
        resource: TResource,
        overrides?: ResourceContextOverrides
    ): TResource;

    protected pruneEmpty<T>(input: T): T {
        return this.utility.stripEmptyValues(input);
    }

    protected buildContextReferences(overrides?: ResourceContextOverrides): ResourceContextReferences {
        const patient = overrides?.patient ?? this.stateService.getPatientReference() ?? undefined;
        const encounter = overrides?.encounter ?? this.stateService.getCurrentEncounterReference() ?? undefined;
        const practitioner = overrides?.practitioner
            ?? (overrides?.practitionerId ? this.stateService.getPractitionerReference(overrides.practitionerId) : null)
            ?? undefined;

        const context: ResourceContextReferences = {};
        if (patient) {
            context.patient = patient;
        }
        if (encounter) {
            context.encounter = encounter;
        }
        if (practitioner) {
            context.practitioner = practitioner;
        }

        return context;
    }

    protected ensureArray<T>(value: T | T[] | null | undefined): T[] {
        return this.utility.ensureArray(value);
    }
}
